/**
 * The directory of every service page.
 *
 * 640 service pages existed but only 17 were linked from anywhere — the rest lived in
 * the sitemap alone, which is why whole locales were never crawled. This is the page
 * that makes them reachable, for a crawler and for a person who wants to see the range.
 */
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { Search } from "lucide-react";
import { useMemo, useState } from "react";

import { TopBar } from "@/components/top-bar";
import { Input } from "@/components/ui/input";
import { SERVICE_NAMES } from "@/lib/hero_services";
import { serviceSlug } from "@/lib/service-slugs";
import { SITE_URL } from "@/lib/site-url";

const validLocales = ["en", "es", "fr", "de", "it", "pt", "zh", "ru"] as const;
type Locale = (typeof validLocales)[number];

// `metaTitle`/`metaDesc` are the <head>; `title`/`sub` are the visible page. They were
// the same English string on all eight locales, so the eight index pages differed only
// by canonical — which is how a set of pages ends up crawled and not indexed.
const UI: Record<
  Locale,
  {
    metaTitle: string;
    metaDesc: string;
    title: string;
    sub: string;
    search: string;
    empty: string;
    count: (n: number) => string;
  }
> = {
  en: {
    metaTitle: "All Services — Virtual Numbers for SMS Verification | SmsEmpire",
    metaDesc:
      "Browse every service you can verify with a temporary virtual number: Telegram, WhatsApp, Instagram, Google and hundreds more. Pay per code, automatic refund.",
    title: "All services with virtual numbers",
    sub: "Pick the service you need to verify. Pay per code, refunded automatically if none arrives.",
    search: "Search a service…",
    empty: "No service matches that search.",
    count: (n) => `${n} services available`,
  },
  es: {
    metaTitle: "Todos los servicios — Números virtuales para verificación SMS | SmsEmpire",
    metaDesc:
      "Explora todos los servicios que puedes verificar con un número virtual temporal: Telegram, WhatsApp, Instagram, Google y cientos más. Pagas por código, con reembolso automático.",
    title: "Todos los servicios con número virtual",
    sub: "Elige el servicio que necesitas verificar. Pagas por código y se te devuelve si no llega.",
    search: "Buscar un servicio…",
    empty: "Ningún servicio coincide con esa búsqueda.",
    count: (n) => `${n} servicios disponibles`,
  },
  fr: {
    metaTitle: "Tous les services — Numéros virtuels pour vérification SMS | SmsEmpire",
    metaDesc:
      "Parcourez tous les services vérifiables avec un numéro virtuel temporaire : Telegram, WhatsApp, Instagram, Google et des centaines d\u2019autres. Paiement au code, remboursement automatique.",
    title: "Tous les services avec numéro virtuel",
    sub: "Choisissez le service à vérifier. Payez par code, remboursé automatiquement s'il n'arrive pas.",
    search: "Rechercher un service…",
    empty: "Aucun service ne correspond.",
    count: (n) => `${n} services disponibles`,
  },
  de: {
    metaTitle: "Alle Dienste — Virtuelle Nummern für die SMS-Verifizierung | SmsEmpire",
    metaDesc:
      "Alle Dienste, die sich mit einer temporären virtuellen Nummer verifizieren lassen: Telegram, WhatsApp, Instagram, Google und Hunderte mehr. Bezahlung pro Code, automatische Erstattung.",
    title: "Alle Dienste mit virtueller Nummer",
    sub: "Wähle den Dienst, den du verifizieren willst. Zahlung pro Code, automatische Rückerstattung.",
    search: "Dienst suchen…",
    empty: "Kein Dienst passt zur Suche.",
    count: (n) => `${n} Dienste verfügbar`,
  },
  it: {
    metaTitle: "Tutti i servizi — Numeri virtuali per la verifica SMS | SmsEmpire",
    metaDesc:
      "Sfoglia tutti i servizi che puoi verificare con un numero virtuale temporaneo: Telegram, WhatsApp, Instagram, Google e centinaia di altri. Paghi a codice, rimborso automatico.",
    title: "Tutti i servizi con numero virtuale",
    sub: "Scegli il servizio da verificare. Paghi per codice, rimborso automatico se non arriva.",
    search: "Cerca un servizio…",
    empty: "Nessun servizio corrisponde.",
    count: (n) => `${n} servizi disponibili`,
  },
  pt: {
    metaTitle: "Todos os serviços — Números virtuais para verificação SMS | SmsEmpire",
    metaDesc:
      "Veja todos os serviços que pode verificar com um número virtual temporário: Telegram, WhatsApp, Instagram, Google e centenas mais. Paga por código, com reembolso automático.",
    title: "Todos os serviços com número virtual",
    sub: "Escolha o serviço a verificar. Paga por código, reembolso automático se não chegar.",
    search: "Procurar um serviço…",
    empty: "Nenhum serviço corresponde.",
    count: (n) => `${n} serviços disponíveis`,
  },
  zh: {
    metaTitle: "全部服务 — 用于短信验证的虚拟号码 | SmsEmpire",
    metaDesc:
      "浏览所有可用临时虚拟号码验证的服务：Telegram、WhatsApp、Instagram、Google 等数百种。按验证码计费，自动退款。",
    title: "支持虚拟号码的全部服务",
    sub: "选择需要验证的服务。按验证码付费，未收到自动退款。",
    search: "搜索服务…",
    empty: "没有匹配的服务。",
    count: (n) => `${n} 个可用服务`,
  },
  ru: {
    metaTitle: "Все сервисы — виртуальные номера для SMS-верификации | SmsEmpire",
    metaDesc:
      "Все сервисы, которые можно подтвердить временным виртуальным номером: Telegram, WhatsApp, Instagram, Google и сотни других. Оплата за код, автоматический возврат.",
    title: "Все сервисы с виртуальным номером",
    sub: "Выберите сервис для верификации. Оплата за код, автоматический возврат, если код не пришёл.",
    search: "Поиск сервиса…",
    empty: "Ничего не найдено.",
    count: (n) => `${n} сервисов доступно`,
  },
};

export const Route = createFileRoute("/$lang/sms/")({
  beforeLoad: ({ params }) => {
    if (!validLocales.includes(params.lang as Locale)) throw notFound();
  },
  component: ServiceIndex,
  head: ({ params }) => {
    const lang = (params.lang as Locale) ?? "en";
    const canonical = `${SITE_URL}/${lang}/sms`;
    const ui = UI[lang] ?? UI.en;
    const title = ui.metaTitle;
    const desc = ui.metaDesc;
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: canonical },
        { property: "og:image", content: `${SITE_URL}/og/sms.png` },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
      ],
      links: [
        { rel: "canonical", href: canonical },
        { rel: "alternate", hrefLang: "x-default", href: `${SITE_URL}/en/sms` },
        ...validLocales.map((l) => ({
          rel: "alternate",
          hrefLang: l,
          href: `${SITE_URL}/${l}/sms`,
        })),
      ],
    };
  },
});

function ServiceIndex() {
  const { lang } = Route.useParams();
  const locale = (validLocales.includes(lang as Locale) ? lang : "en") as Locale;
  const ui = UI[locale];
  const [query, setQuery] = useState("");

  const services = useMemo(
    () =>
      Object.entries(SERVICE_NAMES)
        .map(([code, name]) => ({ code, name: name.trim(), slug: serviceSlug(code) }))
        .filter((s) => s.name)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [],
  );

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return services;
    return services.filter((s) => s.name.toLowerCase().includes(q) || s.slug.includes(q));
  }, [query, services]);

  return (
    <div className="min-h-screen bg-background">
      <TopBar />
      <main className="mx-auto max-w-5xl px-4 py-12 sm:py-16">
        <h1 className="text-4xl font-bold tracking-tight text-foreground sm:text-5xl">
          {ui.title}
        </h1>
        <p className="mt-3 max-w-2xl text-muted-foreground">{ui.sub}</p>

        <div className="relative mt-8 max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={ui.search}
            className="pl-9"
            aria-label={ui.search}
          />
        </div>

        <p className="mt-3 text-sm text-muted-foreground">{ui.count(shown.length)}</p>

        {shown.length === 0 ? (
          <p className="mt-10 rounded-xl border border-dashed border-border p-8 text-center text-muted-foreground">
            {ui.empty}
          </p>
        ) : (
          <ul className="mt-6 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {shown.map((s) => (
              <li key={s.code}>
                <Link
                  to="/$lang/sms/$service"
                  params={{ lang: locale, service: s.slug }}
                  className="block truncate rounded-xl border border-border bg-card px-4 py-3 text-sm font-medium transition-colors hover:border-primary/40 hover:text-primary"
                >
                  {s.name}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}
